import { Skeleton, Toast } from '../ui/index.js'
import styles from './SurfaceState.module.css'

/**
 * `DES-001`. The customer console's own reading of the four surface states the
 * addendum (§A) already defines and `KitchenSinkPage` already demonstrates —
 * loading, empty, error, degraded.
 *
 * Every `/customer/*` screen shipped with `<p>Loading…</p>` and
 * `<p role="alert">…could not be loaded.</p>`, while every operator screen
 * renders skeletons and a bordered error. Same codebase, same primitives
 * sitting one import away, two different answers to the same question.
 *
 * Degraded is deliberately absent here, not forgotten: `RequireTenantAuth`'s
 * own comment already places a customer-facing health signal in `LGH-042`'s
 * dashboard rather than this shell, and that is a ruled placement, not an
 * oversight this task should quietly reverse.
 */

export function SurfaceLoading({ rows = 3 }: { rows?: number }) {
  return (
    <div className={styles['loading']} data-testid="surface-loading">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} width={index === 0 ? '40%' : '100%'} />
      ))}
    </div>
  )
}

export function SurfaceEmpty({ children }: { children: React.ReactNode }) {
  return <p className={styles['empty']}>{children}</p>
}

/**
 * `role="alert"` is kept from the original one-liners — that part was right.
 * What is added is the addendum's own error shape: a bordered surface rather
 * than a sentence indistinguishable from body copy.
 */
export function SurfaceError({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert">
      <Toast variant="error">{children}</Toast>
    </div>
  )
}
